<template>
  <div>
    <van-nav-bar :title="t('security.senha')" :mask="false" :border="false"
      class="custom-nav-bar" >
      <template #left>
        <Button type="back" @click="onClickLeft" />
      </template>
    </van-nav-bar>
    <div class="m-[16px]">
      <div class="text-[13px] text-[#ffffff]">{{ t('security.senhaPage.title') }}</div>
      <div class="color-[#5FB589] text-[12px] mt-[15px]">{{ t('security.senhaPage.newPassword') }}</div>

      <!-- 第一次输入密码 -->
      <div class="flex mt-[15px] items-center justify-center justify-between" @click="focusFirstInput">
        <div v-for="(digit, index) in 6" :key="index"
          class="w-[44px] h-[44px] border border-[#5FB589] rounded-[4px] flex items-center justify-center text-white text-[18px] font-bold bg-[#00563B] cursor-pointer relative"
          :class="{ 'border-[#5FB589] bg-[#5FB589]/20': index < firstPassword.length }">
          {{ firstPassword[index] || '' }}
          <!-- 光标 -->
          <div v-if="index === firstPassword.length && isFirstFocused"
            class="absolute w-[2px] h-[20px] bg-white animate-pulse"></div>
        </div>
      </div>

      <div class="color-[#5FB589] text-[12px] mt-[15px]">{{ t('security.senhaPage.confirmPassword') }}</div>

      <!-- 第二次输入密码 -->
      <div class="flex mt-[15px] items-center justify-center justify-between" @click="focusSecondInput">
        <div v-for="(digit, index) in 6" :key="index"
          class="w-[44px] h-[44px] border border-[#5FB589] rounded-[4px] flex items-center justify-center text-white text-[18px] font-bold bg-[#00563B] cursor-pointer relative"
          :class="{ 'border-[#5FB589] bg-[#5FB589]/20': index < secondPassword.length }">
          {{ secondPassword[index] || '' }}
          <!-- 光标 -->
          <div v-if="index === secondPassword.length && isSecondFocused"
            class="absolute w-[2px] h-[20px] bg-white animate-pulse"></div>
        </div>
      </div>

      <!-- 错误提示 -->
      <div v-if="errorMessage" class="text-red-500 text-[12px] mt-[10px] text-center">
        {{ errorMessage }}
      </div>

      <div class="color-[#5FB589] text-[10px] mt-[15px]">{{ t('security.senhaPage.description') }}</div>

      <!-- 隐藏的输入框用于接收键盘输入 -->
      <input ref="firstInputRef" v-model="firstPassword" type="text" class="opacity-0 absolute pointer-events-none"
        :maxlength="6" @input="onFirstInput" @keydown="onKeydown" @focus="onFirstFocus" @blur="onFirstBlur" />

      <input ref="secondInputRef" v-model="secondPassword" type="text" class="opacity-0 absolute pointer-events-none"
        :maxlength="6" @input="onSecondInput" @keydown="onKeydown" @focus="onSecondFocus" @blur="onSecondBlur" />
    </div>
    <div class="flex justify-center items-center mt-[30px]">
      <div class="anniu" :class="{ 'auth-btn-disabled': !canSubmit }" @click="handleSetPassword">
        {{ isLoading ? t('security.senhaPage.configuring') : t('security.senhaPage.withdrawNow') }}
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { Button } from "@/components/Button"
import { ref, nextTick, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { showToast } from 'vant'
import { setWithdrawPassword } from '@/api/game'

const { t } = useI18n()
const router = useRouter()

const onClickLeft = () => {
  router.push('/security')
}

const firstPassword = ref('')
const secondPassword = ref('')
const firstInputRef = ref<HTMLInputElement>()
const secondInputRef = ref<HTMLInputElement>()
const isFirstFocused = ref(false)
const isSecondFocused = ref(false)
const errorMessage = ref('')
const isLoading = ref(false)

// 计算是否可以提交
const canSubmit = computed(() => {
  return firstPassword.value.length === 6 &&
    secondPassword.value.length === 6 &&
    firstPassword.value === secondPassword.value &&
    !isLoading.value
})

// 处理第一次输入
const onFirstInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  firstPassword.value = target.value.replace(/\D/g, '').slice(0, 6)
  errorMessage.value = ''

  // 如果第一次输入完成，自动聚焦到第二次输入
  if (firstPassword.value.length === 6) {
    nextTick(() => {
      focusSecondInput()
    })
  }
}

// 处理第二次输入
const onSecondInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  secondPassword.value = target.value.replace(/\D/g, '').slice(0, 6)
  errorMessage.value = ''

  // 验证两次密码是否一致
  if (secondPassword.value.length === 6) {
    if (firstPassword.value !== secondPassword.value) {
      errorMessage.value = t('security.senhaPage.errors.passwordsNotMatch')
    }
  }
}

// 处理键盘事件
const onKeydown = (event: KeyboardEvent) => {
  const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab']
  const isNumber = /^[0-9]$/.test(event.key)

  if (!isNumber && !allowedKeys.includes(event.key)) {
    event.preventDefault()
  }
}

// 第一次输入框聚焦事件
const onFirstFocus = () => {
  isFirstFocused.value = true
  isSecondFocused.value = false
}

// 第一次输入框失焦事件
const onFirstBlur = () => {
  isFirstFocused.value = false
}

// 第二次输入框聚焦事件
const onSecondFocus = () => {
  isSecondFocused.value = true
  isFirstFocused.value = false
}

// 第二次输入框失焦事件
const onSecondBlur = () => {
  isSecondFocused.value = false
}

// 聚焦到第一次输入框
const focusFirstInput = () => {
  nextTick(() => {
    firstInputRef.value?.focus()
  })
}

// 聚焦到第二次输入框
const focusSecondInput = () => {
  nextTick(() => {
    secondInputRef.value?.focus()
  })
}

// 组件挂载后自动聚焦到第一次输入
onMounted(() => {
  focusFirstInput()
})

// 设置提现密码
const handleSetPassword = async () => {
  if (!canSubmit.value) {
    return
  }

  // 验证密码长度
  if (firstPassword.value.length !== 6) {
    errorMessage.value = t('security.senhaPage.errors.pleaseEnter6Digits')
    return
  }

  // 验证两次密码是否一致
  if (firstPassword.value !== secondPassword.value) {
    errorMessage.value = t('security.senhaPage.errors.passwordsNotMatch')
    return
  }

  try {
    isLoading.value = true
    firstPassword.value = String(firstPassword.value)
    const response = await setWithdrawPassword(firstPassword.value)

    console.log('设置密码响应:', response)

    // 检查响应格式
    let isSuccess = false
    if (response.code === 0 || response.code === 200) {
      isSuccess = true
    } else if (response.data?.code === 0 || response.data?.code === 200) {
      isSuccess = true
    }

    if (isSuccess) {

      // 根据来源页面决定返回路径
      const fromPage = router.currentRoute.value.query.fromPage as string
      console.log('来源页面:', fromPage)

      if (fromPage === 'wallet') {
        // 如果是从提现页面来的，返回到提现页面
        console.log('返回到提现页面')
        router.push('/wallet')
      } else {
        // 如果是从个人中心来的，返回到安全设置页面
        console.log('返回到安全设置页面')
        router.push('/security')
      }
    } else {
      // 处理错误
    }
  } catch (error) {

  } finally {
    isLoading.value = false
  }
}
</script>

<style lang="scss" scoped>
.custom-nav-bar {
  height: 56px;
  box-shadow: 0px 2px 3px 0px rgba(0,0,0,0.3), 0px -2px 22px 0px rgba(53,189,128,0.61);
  &>* {
    height: 100%;
  }
}
@keyframes pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0;
  }
}

.anniu {
  width: 275px;
  height: 35px;
  background: linear-gradient(0deg, #379753 0%, #46C96D 100%);
  border-radius: 11px;
  color: #fff;
  font-size: 13px;
  text-align: center;
  line-height: 35px;
  font-weight: bold;
  cursor: pointer;
}

.animate-pulse {
  animation: pulse 1s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.auth-btn-disabled {
  background: #126736 !important;
  cursor: not-allowed !important;
  opacity: 0.6;
}

input::placeholder {
  color: #096;
  font-size: 12px;
}
</style>

<route>
{
    name:'senha',
    path:'/security/senha',
    component:()=>import('@/pages/security/senha.vue')
}
</route>
